import {toast} from '@wandb/common/components/elements/Toast';
import {notEmpty} from '@wandb/common/util/obj';
import {makeDidPropsOrStateChange} from '@wandb/common/util/shouldUpdate';
import produce from 'immer';
import * as _ from 'lodash';
import memoize from 'memoize-one';
import * as React from 'react';
import {useMemo} from 'react';
import {useCallback} from 'react';
import {Link, Redirect} from 'react-router-dom';

import BulkTagEditor, {TagState} from '../../components/Tags/BulkTagEditor';
import {
  RunWithRunsetInfo,
  toRunsDataQuery,
  WithRunsDataLoader,
} from '../../containers/RunsDataLoader';
import * as Generated from '../../generated/graphql';
import {
  combinedFilter,
  useRunSelectorQuery,
} from '../../state/graphql/runSelectorQuery';
import {useSelectedCounts} from '../../state/graphql/selectedCountQuery';
import {useDispatch} from '../../state/hooks';
import {useRunsQueryContext} from '../../state/runs/hooks';
import * as RunHooks from '../../state/runs/hooks';
import {setCreateSweepArgs} from '../../state/sweeps/actions';
import * as ViewerHooks from '../../state/viewer/hooks';
import * as CustomRunColorsTypes from '../../state/views/customRunColors/types';
import * as FilterActions from '../../state/views/filter/actions';
import * as GroupSelectionsActions from '../../state/views/groupSelections/actions';
import * as ViewHooks from '../../state/views/hooks';
import * as RunSetActions from '../../state/views/runSet/actions';
import * as RunSetTypes from '../../state/views/runSet/types';
import * as SortActions from '../../state/views/sort/actions';
import * as TempSelectionsActions from '../../state/views/tempSelections/actions';
import * as TempSelectionsTypes from '../../state/views/tempSelections/types';
import {BenchmarkProject} from '../../types/graphql';
import * as Filter from '../../util/filters';
import * as Query from '../../util/queryts';
import {
  Config as TableSettings,
  EMPTY_CONFIG as EMPTY_TABLE_SETTINGS,
} from '../../util/runfeed';
import * as RunHelpers2 from '../../util/runhelpers';
import * as Run from '../../util/runs';
import {formatTimestamp} from '../../util/runs';
import * as SelectionManager from '../../util/selectionmanager';
import {getSweepDisplayName} from '../../util/sweeps';
import * as TableCols from '../../util/tablecols';
import * as urls from '../../util/urls';
import {CreateSweepButton} from '../CreateSweepButton';
import {DeleteRunsButton} from '../DeleteRunsButton';
import {EditableLabel} from '../elements/EditableLabel';
import {TimeDisplay} from '../elements/TimeDisplay';
import {ProjectPicker} from '../ProjectPicker';
import {RunMover} from '../RunMover';
import {RunsSearchBar} from '../RunsSearchBar';
import {TagAddButton} from '../TagAddButton';
import {Tags} from '../Tags';
import {WBTable, WBTableComp, WBTableProps} from '../WBTable/WBTable';
import {WBTableCellCreatedAt} from '../WBTable/WBTableCellCreatedAt';
import {WBTableCellDefault} from '../WBTable/WBTableCellDefault';
import {RunsAutoCols} from './RunsAutoCols';
import {RunsColumnEditor} from './RunsColumnEditor';
import {RunsDownloadMenu} from './RunsDownloadMenu';
import * as S from './RunSelectorInner.styles';
import {RunSelectorRowHeader} from './RunSelectorRowHeader';
import {RunsFreezeAndFilter} from './RunsFreeze';
import {RunsGroupTableAction} from './RunsGrouping';
import {RunsSortTableAction} from './RunsSort';
import {makeRunsSortIndicator} from './RunsSortIndicator';
import {RunsTableHeader} from './RunsTableHeader';

/*
 * RunSelectorInner is WBTable, wrapped with RunSelectorInnerQuery (to fetch tags)
 * There is one top-level RunsDataLoader query, and one query for each expanded group (see <WBTableRow>)
 */

const FIXED_COLUMNS = ['run:displayName'];
const CELL_FILTER_BLACKLIST: Run.Key[] = [
  {section: 'run', name: 'github'},
  {section: 'run', name: 'runInfo.gpu'},
  {section: 'run', name: 'runInfo.gpuCount'},
];

type RunSelectorFeature =
  | 'create-sweep'
  | 'push-to-run-queue'
  | 'toggle-visibility'
  | 'filter'
  | 'group'
  | 'sort'
  | 'tag'
  | 'move'
  | 'delete'
  | 'export'
  | 'auto-cols'
  | 'edit-cols';
export type DisabledFeaturesMap = {[F in RunSelectorFeature]?: boolean};

interface TopLevelQueryVariables {
  entityName: string;
  projectName: string;
  grouping: Query.Grouping;
  columnOrder: string[];
  queryVariables: Query.Query;
  isTableCollapsed: boolean;
  columnPinned: {[key: string]: boolean};
}

interface RunSelectorInnerProps {
  className?: string;
  pageEntityName: string;
  pageProjectName: string;
  isExpandable?: boolean;
  isExpanded?: boolean;
  defaultKeys?: Run.Key[]; // fka baseKeys
  pollInterval: number;
  benchmarkMeta?: BenchmarkProject;
  displayBenchmark?: boolean;
  customRunColorsRef: CustomRunColorsTypes.Ref;
  runSetRef: RunSetTypes.Ref;
  tempSelectionsRef: TempSelectionsTypes.Ref;
  title?: string;
  isSingleMode?: boolean;
  selectedRunName?: string;
  onSelectRunName?: (runId: string) => void;
  enableSetProject?: boolean;
  enableFreezeRunset?: boolean;
  recommendedGrouping?: Query.Grouping;
  showArtifactCounts?: boolean;
  showLogCounts?: boolean;
  onSetExpanded?: (expanded: boolean) => void;
  isInReport?: boolean;

  // If true, users won't be able to edit filter, grouping, etc.
  readOnly?: boolean;
  // If true, users won't be able to rename runs, etc.
  noEdit?: boolean;

  disabledFeatures?: DisabledFeaturesMap;
  disableRunLinks?: boolean;
  onHeightChange?(): void;
}

type AllRunSelectorProps = RunSelectorInnerProps &
  ReturnType<typeof useRunSelectorProps>;

interface RunSelectorInnerState {
  reloadIndex: number;
  tableMessage?: JSX.Element;
  creatingSweep: boolean;
  currentPage: number;
  sortPopupOpen: boolean;
}

class RunSelectorInnerComp extends React.Component<
  AllRunSelectorProps,
  RunSelectorInnerState
> {
  state: RunSelectorInnerState = {
    reloadIndex: 0,
    tableMessage: undefined,
    creatingSweep: false,
    currentPage: 1,
    sortPopupOpen: false,
  };

  didPropsOrStateChange = makeDidPropsOrStateChange({
    name: 'RunSelector',
    debug: false,
    verbose: true,
  });

  tableRef = React.createRef<WBTableComp>();

  filteredKeys = memoize(
    (paths: string[] | undefined): Run.Key[] => {
      if (paths == null) {
        return [];
      }
      const suggested = RunHelpers2.keySuggestions(paths);
      if (suggested != null) {
        return TableCols.filterLegacyKeys(
          suggested.map(Run.keyFromString).filter(notEmpty)
        );
      }
      return [];
    },
    // Note: We're using string length comparison as a check here, because this can
    // be a very long string (one customer has 4000 columns with long names, which makes
    // this string 900kB)
    (newArgs, oldArgs) => {
      if (!Array.isArray(newArgs) || !Array.isArray(oldArgs)) {
        return false;
      }

      const [newPaths] = newArgs;
      const [oldPaths] = oldArgs;
      if (newPaths == null && oldPaths == null) {
        return true;
      }
      if (newPaths != null && oldPaths != null) {
        return newPaths.length === oldPaths.length;
      }
      return false;
    }
  );

  cleanedTableSettings = memoize(
    (
      tableSettings: TableSettings,
      filteredKeys: Run.Key[],
      defaultKeys: Run.Key[],
      hideNewKey?: boolean
    ) => {
      return TableCols.cleanRunFeedConfig(
        tableSettings,
        filteredKeys,
        defaultKeys,
        hideNewKey
      );
    }
  );

  getTopLevelQueryVariables = memoize(
    (args: TopLevelQueryVariables) => {
      const {
        entityName,
        projectName,
        grouping,
        columnOrder,
        queryVariables,
        isTableCollapsed,
        columnPinned,
      } = args;
      let queryColumnOrder = columnOrder;
      if (isTableCollapsed) {
        // If the table is collapsed, only fetch the pinned columns.
        const numPinnedColumns = _.sum(Object.values(columnPinned));
        queryColumnOrder = columnOrder.slice(0, numPinnedColumns);
      }
      const fetchColumns: Run.Key[] = [
        ...grouping,
        ...(queryColumnOrder
          .map(Run.keyFromString)
          .filter(key => !!key) as Run.Key[]),
        ...(queryVariables.selections != null
          ? Filter.getUniqueKeysUsedInFilter(queryVariables.selections)
          : []),
      ];

      const configKeys = fetchColumns
        .filter(key => key.section === 'config')
        .map(key => key.name);
      const summaryKeys = fetchColumns
        .filter(key => key.section === 'summary')
        .map(key => key.name);
      const wandbKeys = ['cli_version', 'is_jupyter_run'];

      // Merge state.searchFilter with current filters
      const topLevelQueryVariables: Query.Query = {
        ...queryVariables,
        entityName,
        projectName,
        filters: Query.addSearchFilter(
          queryVariables.filters,
          this.props.runSet.search.query,
          queryVariables.grouping
        ),
        configKeys,
        summaryKeys,
        wandbKeys,
      };
      return topLevelQueryVariables;
    },
    (newArgs, oldArgs) => {
      if (!Array.isArray(newArgs) || !Array.isArray(oldArgs)) {
        return false;
      }
      const {
        entityName,
        projectName,
        grouping,
        columnOrder,
        queryVariables,
        isTableCollapsed,
      } = newArgs[0];
      const {
        entityName: oldEntityName,
        projectName: oldProjectName,
        grouping: oldGrouping,
        columnOrder: oldColumnOrder,
        queryVariables: oldQueryVariables,
        isTableCollapsed: oldIsTableCollapsed,
      } = oldArgs[0];
      return (
        entityName === oldEntityName &&
        projectName === oldProjectName &&
        grouping === oldGrouping &&
        columnOrder === oldColumnOrder &&
        queryVariables === oldQueryVariables &&
        isTableCollapsed === oldIsTableCollapsed
      );
    }
  );

  rows = memoize((rowData: RunWithRunsetInfo[], selectedRunName?: string) => {
    if (rowData.some(r => r.state === 'running')) {
      window.analytics?.track('Active Run Viewed', {
        location: 'runs table',
      });
    }
    return rowData.map(r => {
      return {
        ...r,
        selectedRunName,
        __address__: TableCols.makeRowAddressComponent(r.name, r.runsetInfo.id),
      };
    });
  });

  lastRowCount = 0;

  allColumns = memoize(
    (
      entityName: string,
      projectName: string,
      defaultKeys: Run.Key[],
      filteredKeys: Run.Key[],
      tableSettings: TableSettings,
      totalRowCount: number,
      firstActiveRunName: string | undefined,
      benchmarkMeta: BenchmarkProject | undefined,
      displayBenchmark: boolean | undefined
    ) => {
      const {setTableSettings, selectAll, selectNone, disableRunLinks} =
        this.props;
      const filteredKeyStrings = filteredKeys.map(k => Run.keyToString(k));
      const orderKeys = tableSettings.columnOrder
        .filter(k => !_.includes(filteredKeyStrings, k))
        .map(k => Run.keyFromString(k)!);
      return TableCols.unionKeys(defaultKeys, [
        ...filteredKeys,
        ...orderKeys,
      ]).map(key => {
        return TableCols.keyToColumn(key, {
          renderHeader: Run.keysEqual(key, {
            section: 'run',
            name: 'displayName',
          })
            ? (displayedRows: Run.Run[]) => {
                return (
                  <RunsTableHeader
                    isSingleMode={this.props.isSingleMode}
                    totalRowCount={totalRowCount}
                    displayedRows={displayedRows}
                    runSetRef={this.props.runSetRef}
                    tempSelectionsRef={this.props.tempSelectionsRef}
                    showTempSelection={
                      !this.props.isExpandable || !!this.props.isExpanded
                    }
                    onlyShowSelected={tableSettings.onlyShowSelected}
                    readOnly={this.props.readOnly}
                    disabledFeatures={this.props.disabledFeatures}
                    isInReport={this.props.isInReport}
                    onAllItemsTempSelected={() => {
                      // Show the "select all runs in project?" prompt.
                      // TODO(views): This could maybe be factored out somehow.
                      if (totalRowCount > displayedRows.length) {
                        const isGrouped = !_.isEmpty(
                          this.props.groupSelections.grouping
                        );
                        this.setState({
                          tableMessage: (
                            <>
                              All <strong>{displayedRows.length}</strong>
                              {isGrouped ? ' groups ' : ' runs '} on this page
                              are selected.{' '}
                              <span
                                className="fake-link"
                                onClick={() => {
                                  this.props.tempSelectAll();
                                  this.clearTableMessage();
                                }}>
                                Select all
                                {isGrouped ? ' groups ' : ' runs '} in this
                                project
                              </span>
                            </>
                          ),
                        });
                      }
                    }}
                    onAllItemsTempUnselected={this.clearTableMessage}
                    // TODO: Cleanup step: move these callbacks into RunsTableHeader.
                    onVisibilitySelectNone={selectNone}
                    onVisibilitySelectAll={selectAll}
                    onSetOnlyShowSelected={newSetting => {
                      setTableSettings({
                        ...tableSettings,
                        onlyShowSelected: newSetting,
                      });
                      this.reload();
                    }}
                  />
                );
              }
            : undefined,
          renderCell: (
            column,
            row: RunHelpers2.WBTableRowWithRunSetInfo,
            {
              addFilter,
              toggleExpandedRow,
              recursionDepth,
              isGroup,
              isGroupExpanded,
              loadingChildren,
              isHoveringRow,
            }
          ) => {
            const isGroupRun = isGroup && row.group === row.name;
            const columnKey = column.key;
            const value = Run.getValue(row, columnKey);
            if (CELL_FILTER_BLACKLIST.some(f => Run.keysEqual(columnKey, f))) {
              return (
                <WBTableCellDefault columnKey={columnKey} cellValue={value} />
              );
            } else if (columnKey.section === 'tags') {
              const allTags =
                !this.props.runSelectorQuery.initialLoading &&
                this.props.runSelectorQuery.project.tags;
              return (isGroup && !isGroupRun) || !allTags ? (
                ''
              ) : (
                <>
                  <Tags
                    tags={row.tags}
                    onClick={tag =>
                      !this.props.readOnly &&
                      addFilter &&
                      addFilter({section: 'tags', name: tag}, '=', true)
                    }
                    enableDelete={!this.props.readOnly}
                    deleteTag={tag =>
                      isGroup
                        ? this.deleteGroupTag(row, tag.name)
                        : this.deleteTag(row, tag.name)
                    }
                  />
                  {!this.props.readOnly && (
                    <div className="icon-wrap">
                      <TagAddButton
                        compact
                        direction="bottom right"
                        tags={row.tags}
                        availableTags={allTags}
                        addTag={async tag =>
                          isGroup
                            ? this.addGroupTag(row, tag.name)
                            : this.addTag(row, tag.name)
                        }
                        iconOnly
                      />
                    </div>
                  )}
                </>
              );
            } else if (
              Run.keysEqual(columnKey, {section: 'run', name: 'createdAt'})
            ) {
              return (
                <WBTableCellCreatedAt
                  createdAt={row.createdAt}
                  addFilter={addFilter}
                />
              );
            } else if (
              Run.keysEqual(columnKey, {section: 'run', name: 'heartbeatAt'})
            ) {
              return (
                <WBTableCellDefault
                  columnKey={columnKey}
                  cellValue={formatTimestamp(row.heartbeatAt)}
                  addFilter={addFilter}
                />
              );
            } else if (
              Run.keysEqual(columnKey, {section: 'run', name: 'updatedAt'})
            ) {
              return (
                <WBTableCellDefault
                  columnKey={columnKey}
                  cellValue={formatTimestamp(row.updatedAt)}
                  addFilter={addFilter}
                />
              );
            } else if (
              Run.keysEqual(columnKey, {section: 'run', name: 'duration'})
            ) {
              return (
                <TimeDisplay
                  timestamp={new Date(row.createdAt)}
                  format="month_round"
                  now={new Date(row.heartbeatAt)}
                />
              );
            } else if (
              Run.keysEqual(columnKey, {section: 'run', name: 'notes'})
            ) {
              const readOnlyNotes =
                (isGroup && !isGroupRun) || this.props.readOnly;
              return (
                <EditableLabel
                  data-test="add-notes-input"
                  placeholder={readOnlyNotes ? '-' : 'Add notes...'}
                  readOnly={readOnlyNotes}
                  onBlur={(newNotes: string) => {
                    isGroupRun
                      ? this.saveRunGroupNotes(row, newNotes)
                      : this.saveRunNotes(row, newNotes);
                  }}
                  serverText={row.notes}
                  showPopup
                />
              );
            } else if (
              Run.keysEqual(columnKey, {
                section: 'run',
                name: 'sweep',
              })
            ) {
              return (
                <>
                  {row.sweep ? (
                    disableRunLinks ? (
                      <WBTableCellDefault
                        columnKey={columnKey}
                        cellValue={getSweepDisplayName(row.sweep)}
                        addFilter={addFilter}
                      />
                    ) : (
                      <Link
                        to={urls.sweep({
                          entityName,
                          projectName,
                          sweepName: row.sweep.name,
                        })}>
                        {getSweepDisplayName(row.sweep)}
                      </Link>
                    )
                  ) : (
                    '-'
                  )}
                </>
              );
            } else if (
              Run.keysEqual(columnKey, {
                section: 'run',
                name: 'displayName',
              })
            ) {
              return (
                <RunSelectorRowHeader
                  entityName={entityName}
                  projectName={projectName}
                  isTableCollapsed={
                    this.props.isExpandable && !this.props.isExpanded
                  }
                  recursionDepth={recursionDepth}
                  row={row}
                  customRunColorsRef={this.props.customRunColorsRef}
                  loadingChildren={loadingChildren}
                  isGroupExpanded={!!isGroupExpanded}
                  isSingleMode={this.props.isSingleMode}
                  showArtifactCounts={this.props.showArtifactCounts}
                  showLogCounts={this.props.showLogCounts}
                  selectedRunName={this.props.selectedRunName}
                  onSelectRunName={this.props.onSelectRunName}
                  isGroup={isGroup}
                  groupSelectionsRef={this.props.runSet.groupSelectionsRef}
                  tempSelectionsRef={this.props.tempSelectionsRef}
                  benchmarkMeta={benchmarkMeta}
                  displayBenchmark={displayBenchmark}
                  displayTempCheckbox={!!isHoveringRow}
                  readOnly={this.props.readOnly}
                  noEdit={this.props.noEdit}
                  disabledFeatures={this.props.disabledFeatures}
                  disableRunLink={disableRunLinks}
                  isFirstActiveRun={row.name === firstActiveRunName}
                  isInReport={this.props.isInReport}
                  // Actual group selection logic is now pushed down to this component.
                  // We just want to know about changes so we can call hintClose.
                  // TODO(views): There is probably a better pattern for this?
                  onVisibilityCheckboxClick={() => {
                    if (this.tableRef && this.tableRef.current) {
                      this.tableRef.current.hintClose();
                    }
                  }}
                  onTempCheckboxClick={this.clearTableMessage}
                  onGroupButtonClick={() => {
                    if (isGroup) {
                      return (
                        toggleExpandedRow && toggleExpandedRow(row.__address__)
                      );
                    }
                  }}
                  saveRunName={this.saveRunName}
                  deleteRun={(id, deleteArtifacts) =>
                    this.props
                      .deleteRun({variables: {id, deleteArtifacts}})
                      .then(this.reload)
                  }
                  stopRun={id =>
                    this.props.stopRun({variables: {id}}).then(this.reload)
                  }
                />
              );
            } else if (value != null) {
              return (
                <WBTableCellDefault
                  columnKey={columnKey}
                  cellValue={value}
                  addFilter={addFilter}
                />
              );
            } else {
              return '-';
            }
          },
        });
      });
    },
    (newArgs, oldArgs) => {
      if (
        !Array.isArray(newArgs) ||
        !Array.isArray(oldArgs) ||
        newArgs.length !== oldArgs.length
      ) {
        return false;
      }
      const [
        entityName,
        projectName,
        defaultKeys,
        filteredKeys,
        tableSettings,
        totalRowCount,
        benchmarkMeta,
        displayBenchmark,
      ] = newArgs;
      const [
        oldEntityName,
        oldProjectName,
        oldDefaultKeys,
        oldFilteredKeys,
        oldTableSettings,
        oldTotalRowCount,
        oldBenchmarkMeta,
        oldDisplayBenchmark,
      ] = oldArgs;
      return (
        entityName === oldEntityName &&
        projectName === oldProjectName &&
        _.isEqual(defaultKeys, oldDefaultKeys) &&
        filteredKeys === oldFilteredKeys &&
        tableSettings === oldTableSettings &&
        totalRowCount === oldTotalRowCount &&
        _.isEqual(benchmarkMeta, oldBenchmarkMeta) &&
        displayBenchmark === oldDisplayBenchmark
      );
    }
  );

  getExtraTableActionsLeft = memoize(
    (
      entityName,
      hasEntityStorageEnabled,
      projectName,
      enableSetProject,
      enableFreezeRunset,
      groupSelections,
      username,
      tempSelectNone,
      tags,
      showOverflowButton,
      disabledFeatures
    ) => (
      <>
        {enableSetProject && (
          <ProjectPicker
            entityName={entityName}
            projectName={projectName}
            setProject={this.props.setProject}
          />
        )}
        <RunsSearchBar
          isGroupSearch={Query.isGroupedByGroup(groupSelections.grouping)}
          isInReport={this.props.isInReport}
          searchQuery={this.props.runSet.search.query}
          setSearch={query => {
            if (this.tableRef.current) {
              this.tableRef.current.resetPagination();
            }
            this.props.setSearch(query);
          }}
        />
        <S.TableActionButtonsWrapper>
          <RunsFreezeAndFilter
            enableFreezeRunset={enableFreezeRunset}
            filtersRef={this.props.runSet.filtersRef}
            entityName={entityName}
            isInReport={this.props.isInReport}
            projectName={projectName}
            username={username}
            compact={!!this.props.isExpandable && !this.props.isExpanded}
            onChange={() => {
              if (this.tableRef.current != null) {
                this.tableRef.current.resetPagination();
              }
            }}
          />
          <RunsGroupTableAction
            entityName={entityName}
            projectName={projectName}
            groupSelectionsRef={this.props.runSet.groupSelectionsRef}
            recommendedGrouping={this.props.recommendedGrouping}
            compact={!!this.props.isExpandable && !this.props.isExpanded}
            isInReport={this.props.isInReport}
            onGroupingChanged={() => {
              this.tableRef.current?.resetPagination();
              tempSelectNone();
            }}
          />
          <RunsSortTableAction
            entityName={entityName}
            projectName={projectName}
            sortRef={this.props.runSet.sortRef}
            compact={!!this.props.isExpandable && !this.props.isExpanded}
            isInReport={this.props.isInReport}
            open={this.state.sortPopupOpen}
            onOpen={() => this.setState({sortPopupOpen: true})}
            onClose={() => this.setState({sortPopupOpen: false})}
            onSortChanged={() =>
              this.tableRef.current && this.tableRef.current.resetPagination()
            }
          />
          {this.props.isExpandable && !this.props.isExpanded ? (
            <></>
          ) : (
            <>
              {/* BATCH TAGGING, MOVING, DELETING */}
              <BulkTagEditor
                // TODO(views): Here and below, this is a bad way to pass these counts through as it
                // won't always update.
                selectedCount={this.props.tempSelectedCount}
                isInReport={this.props.isInReport}
                tags={tags}
                addTag={(tag: string) => this.bulkUpdateTag('add', tag)}
                removeTag={(tag: string) => this.bulkUpdateTag('remove', tag)}
              />
              <RunMover
                entityName={entityName}
                // prevent users from trying to move runs to another entity if the entity is using external storage
                // because this operation does not move the run files
                requireSameEntity={hasEntityStorageEnabled}
                requireSameEntityReason={
                  "Can't move runs between entities when using external storage"
                }
                isInReport={this.props.isInReport}
                projectName={projectName}
                selectedCount={this.props.tempSelectedCount}
                refetch={() => this.reload()}
                onMove={(entity, proj) => this.moveRuns(entity, proj)}
              />
              <DeleteRunsButton
                selectedCount={this.props.tempSelectedCount}
                isInReport={this.props.isInReport}
                onDelete={(deleteArtifacts: boolean) =>
                  this.deleteRuns(deleteArtifacts)
                }
                outputArtifactsCount={this.props.tempOutputArtifactsCount}
                activeRunsCount={this.props.tempSelectedActiveCount}
              />
              {!disabledFeatures?.['create-sweep'] && (
                <CreateSweepButton onClick={this.createSweepWithPriorRuns} />
              )}
            </>
          )}
        </S.TableActionButtonsWrapper>
      </>
    )
  );

  getExtraTableActionsRight = memoize(
    (
      allQueryVariables,
      topLevelQuery,
      allColumns,
      cleanedTableSettings,
      currentPage
    ) => {
      return (
        <>
          <RunsDownloadMenu
            entityName={this.props.pageEntityName}
            projectName={this.props.pageProjectName}
            pageQuery={allQueryVariables}
            query={topLevelQuery}
            tableSettings={cleanedTableSettings}
          />
          <RunsAutoCols
            query={allQueryVariables}
            tableSettings={cleanedTableSettings}
            isInReport={this.props.isInReport}
            setTableSettings={this.props.setTableSettings}
            currentPage={currentPage}
            columns={allColumns}
          />
        </>
      );
    }
  );

  saveRunNotes = _.debounce((run: Run.Run, newNotes: string) => {
    const currentNotes = run.notes;
    if (newNotes !== currentNotes) {
      // Sending undefined values for displayName or notes clears them, so
      // we always need to send both.
      this.props.updateRun(run.id, run.name, {
        displayName: run.displayName,
        notes: newNotes,
      });
    }
  }, 500);

  saveRunGroupNotes = _.debounce((run: Run.Run, newNotes: string) => {
    const {group: name, tags} = run;
    const {entityName, projectName} = this.props;
    this.props.updateGroupRun({
      variables: {
        projectName,
        entityName,
        name,
        tags: tags.map(t => t.name),
        notes: newNotes,
      },
    });
  }, 500);

  updateSort = (updateFn: (sort: Query.Sort) => void) => {
    const {sort, setSort} = this.props;
    const newSort = produce(sort, updateFn);
    setSort(newSort);
  };

  clearTableMessage = (callback?: () => void) => {
    this.setState({tableMessage: undefined}, () => {
      if (callback) {
        callback();
      }
    });
  };

  shouldComponentUpdate(
    nextProps: RunSelectorInnerProps,
    nextState: RunSelectorInnerState
  ): boolean {
    return this.didPropsOrStateChange(
      [this.props, nextProps],
      [this.state, nextState]
    );
  }

  saveRunName = (run: Run.Run, newName: string) => {
    const currentName = run.displayName || run.name;
    if (newName !== currentName) {
      // Sending undefined values for displayName or notes clears them, so
      // we always need to send both.
      this.props.updateRun(run.id, run.name, {
        displayName: newName,
        notes: run.notes,
      });
      window.analytics?.track('Run name edited');
    }
  };

  addTag = (run: Run.Run, newTagName: string) => {
    const currentTagNames = run.tags.map(t => t.name);
    if (!_.includes(currentTagNames, newTagName)) {
      this.props.updateRunTags(run, [...currentTagNames, newTagName]);
    }
  };

  addGroupTag = (run: Run.Run, newTagName: string) => {
    const currentTagNames = run.tags.map(t => t.name);
    if (currentTagNames.includes(newTagName)) {
      return;
    }

    this.props.updateGroupRunTags(run, [...currentTagNames, newTagName]);
  };

  deleteTag = (run: Run.Run, removeTagName: string) => {
    const currentTagNames = run.tags.map(t => t.name);
    if (_.includes(currentTagNames, removeTagName)) {
      this.props.updateRunTags(
        run,
        currentTagNames.filter(t => t !== removeTagName)
      );
    }
  };

  deleteGroupTag = (run: Run.Run, removeTagName: string) => {
    const currentTagNames = run.tags.map(t => t.name);
    if (!currentTagNames.includes(removeTagName)) {
      return;
    }
    this.props.updateGroupRunTags(
      run,
      currentTagNames.filter(t => t !== removeTagName)
    );
  };

  bulkUpdateTag(actionType: 'add' | 'remove', tagName: string): void {
    const {entityName, projectName} = this.props;
    const queryFilters = this.combinedFilter();
    this.props
      .bulkUpdateRunTags({
        entityName,
        projectName,
        filters: JSON.stringify(queryFilters),
        [actionType === 'add' ? 'addTags' : 'removeTags']: [tagName],
      })
      .then(() => {
        // Refreshes the <BulkTagEditor> menu
        if (
          !this.props.runSelectorQuery.loading &&
          this.props.runSelectorQuery.refetch
        ) {
          this.props.runSelectorQuery.refetch();
        }
      });
  }

  moveRuns(
    destinationEntityName: string,
    destinationProjectName: string
  ): Promise<string> {
    const {entityName, projectName} = this.props;
    const queryFilters = this.combinedFilter();

    return this.props
      .moveRuns({
        variables: {
          sourceEntityName: entityName,
          sourceProjectName: projectName,
          destinationEntityName,
          destinationProjectName,
          filters: JSON.stringify(queryFilters),
        },
      })
      .then(response => {
        if (response.data == null || response.data.moveRuns == null) {
          throw new Error('Unexpected response');
        }
        return response.data.moveRuns.task.id;
      });
  }

  deleteRuns(deleteArtifacts: boolean): void {
    const {entityName, projectName} = this.props;
    const queryFilters = this.combinedFilter();

    this.props
      .deleteRuns({
        variables: {
          entityName,
          projectName,
          filters: JSON.stringify(queryFilters),
          deleteArtifacts,
        },
      })
      .then(resp => {
        if (resp.data?.deleteRuns?.errors) {
          toast(resp.data.deleteRuns.errors, {type: 'error'});
        }
        this.reload();
      });
  }

  createSweepWithPriorRuns = () => this.setState({creatingSweep: true});

  combinedFilter = () => {
    const {filters, mergeFilters, runSet} = this.props;
    const groupTempSelection = this.props.groupTempSelection;

    return combinedFilter(
      filters,
      groupTempSelection,
      undefined,
      mergeFilters,
      runSet.search.query
    );
  };

  reload = () => {
    this.setState({
      reloadIndex: this.state.reloadIndex + 1,
    });
    if (
      !this.props.runSelectorQuery.loading &&
      this.props.runSelectorQuery.refetch
    ) {
      this.props.runSelectorQuery.refetch();
    }
  };

  columnEditor = (
    config: TableSettings,
    setTableSettings: (config: TableSettings) => void
  ) => {
    const {entityName, projectName} = this.props;
    return (
      <RunsColumnEditor
        entityName={entityName}
        projectName={projectName}
        config={config}
        fixedColumns={FIXED_COLUMNS}
        update={setTableSettings}
      />
    );
  };

  onSetCurrentPage = (currentPage: number) => {
    this.setState({currentPage});
  };

  openSortPopup = () => this.setState({sortPopupOpen: true});

  render() {
    const {
      entityName,
      projectName,
      tableSettings,
      queryVariables,
      benchmarkMeta,
      displayBenchmark,
      setGrouping,
      enableSetProject,
      enableFreezeRunset,
      groupSelections,
      report,
      runSetFull,
      tempSelections,
      tempSelectNone,
      dispatch,
      disabledFeatures,
      sweep,
      onHeightChange,
    } = this.props;
    const {reloadIndex, tableMessage, creatingSweep, currentPage} = this.state;

    if (creatingSweep) {
      dispatch(
        setCreateSweepArgs({
          runSet: runSetFull,
          tempSelections,
        })
      );
      window.analytics?.track('Create Sweep Clicked', {
        entityName,
        projectName,
        location: 'runs table',
      });
      return (
        <Redirect to={`/${entityName}/${projectName}/create-sweep`} push />
      );
    }

    const defaultKeys =
      this.props.defaultKeys ??
      (sweep == null
        ? TableCols.DEFAULT_RUNSELECTOR_KEYS
        : sweep.earlyTerminate
        ? TableCols.DEFAULT_SWEEP_EARLY_TERMINATE_KEYS
        : TableCols.DEFAULT_SWEEP_KEYS); // fka baseKeys

    const runSelectorQuery = this.props.runSelectorQuery;
    const username = this.props.viewer ? this.props.viewer.username : '';

    const project = runSelectorQuery.initialLoading
      ? undefined
      : runSelectorQuery.project;
    const filteredKeys = this.filteredKeys(
      project ? project.fields.edges.map(e => e.node.path) : undefined
    );
    const hasEntityStorageEnabled =
      project?.entity.settings?.storageBucketInfo != null;

    let tags: TagState[] = [];
    if (project) {
      const counts: {[key: string]: number} = {};
      project.tagCounts.forEach(t => {
        counts[t.name] = t.count;
      });
      tags = project.tags.map(tag => ({
        name: tag.name,
        colorIndex: tag.colorIndex,
        count: counts[tag.name] || 0,
      }));
    }

    const cleanedTableSettings = this.cleanedTableSettings(
      tableSettings || EMPTY_TABLE_SETTINGS,
      filteredKeys,
      defaultKeys,
      report != null
    );

    const isTableCollapsed = this.props.isExpandable
      ? !this.props.isExpanded
      : false;

    const topLevelQueryVariables = this.getTopLevelQueryVariables({
      entityName,
      projectName,
      grouping: groupSelections.grouping,
      columnOrder: cleanedTableSettings.columnOrder,
      queryVariables,
      isTableCollapsed,
      columnPinned: cleanedTableSettings.columnPinned,
    });

    const topLevelQuery = toRunsDataQuery(
      topLevelQueryVariables,
      {selectionsAsFilters: Boolean(cleanedTableSettings!.onlyShowSelected)},
      {
        page: {
          size: cleanedTableSettings.pageSize || EMPTY_TABLE_SETTINGS.pageSize,
        },
        disabled: runSelectorQuery.initialLoading,
        historyKeyInfo: false,
      }
    );

    const showOverflowButton = !disabledFeatures?.['create-sweep'];

    return (
      <>
        <WithRunsDataLoader key={reloadIndex} query={topLevelQuery}>
          {injectedProps => {
            const {data, loading} = injectedProps;
            const rowData = data && data.filtered;
            const {selectedRunName} = this.props;
            const rows = this.rows(rowData, selectedRunName);
            const firstActiveRunName = rowData.find(
              r => r.state === 'running'
            )?.name;
            const totalRowCount = data && data.totalRuns;
            const allColumns = this.allColumns(
              entityName,
              projectName,
              defaultKeys,
              filteredKeys,
              cleanedTableSettings,
              totalRowCount,
              firstActiveRunName,
              benchmarkMeta,
              displayBenchmark
            );

            if (rows.length !== this.lastRowCount) {
              this.lastRowCount = rows.length;
              onHeightChange?.();
            }

            const tableProps: WBTableProps = {
              className:
                'run-selector' +
                (this.props.className ? ` ${this.props.className}` : '') +
                (this.props.isExpandable ? ` expandable` : '') +
                (this.props.readOnly ? ` read-only` : ''),
              tableSettings: cleanedTableSettings,
              rows,
              columns: allColumns,
              fixedColumns: FIXED_COLUMNS,
              totalRowCount,
              loading: loading || data.initialLoading,
              tableLoadMore: data && data.loadMore,
              expandedRowAddresses: groupSelections.expandedRowAddresses,
              toggleExpandedRow: this.props.toggleExpandedRow,
              grouping: groupSelections.grouping,
              filters: this.props.filters,
              SortIndicatorComponent: makeRunsSortIndicator(
                this.props.runSet.sortRef
              ),
              setTableSettings: this.props.setTableSettings,
              updateSort: this.updateSort,
              setFilters: this.props.setFilters,
              expandable: this.props.isExpandable,
              expanded: this.props.isExpanded,
              isSingleMode: this.props.isSingleMode,
              showArtifactCounts: this.props.showArtifactCounts,
              showLogCounts: this.props.showLogCounts,
              isInReport: this.props.isInReport,
              title: this.props.title,
              onSetExpanded: this.props.onSetExpanded,
              readOnly: this.props.readOnly,
              setGrouping,
              tableMessage,
              clearTableMessage: this.clearTableMessage,
              extraTableActionsLeft: this.getExtraTableActionsLeft(
                entityName,
                hasEntityStorageEnabled,
                projectName,
                enableSetProject,
                enableFreezeRunset,
                groupSelections,
                username,
                tempSelectNone,
                tags,
                showOverflowButton,
                disabledFeatures
              ),
              extraTableActionsRight: this.getExtraTableActionsRight(
                topLevelQueryVariables,
                topLevelQuery,
                allColumns,
                cleanedTableSettings,
                currentPage
              ),
              columnEditor: this.columnEditor,
              onSetCurrentPage: this.onSetCurrentPage,
              openSortPopup: this.openSortPopup,
              topLevelQueryVariables,
            };
            return <WBTable tableRef={this.tableRef} {...tableProps} />;
          }}
        </WithRunsDataLoader>
      </>
    );
  }
}

function useRunSelectorProps(props: RunSelectorInnerProps) {
  const viewer = ViewerHooks.useViewer();
  const runSet = ViewHooks.usePart(props.runSetRef);
  const runSetFull = ViewHooks.useWhole(props.runSetRef);

  const groupSelections = ViewHooks.useWhole(runSet.groupSelectionsRef);
  const tempSelections = ViewHooks.useWhole(props.tempSelectionsRef);
  const runSetFilters = ViewHooks.useWhole(runSet.filtersRef);
  const sort = runSetFull.sort;

  const toggleExpandedRow = ViewHooks.useViewAction(
    runSet.groupSelectionsRef,
    GroupSelectionsActions.toggleExpandedRowAddress
  );

  const tempSelectAll = ViewHooks.useViewActionBindAll(
    TempSelectionsActions.selectAll,
    props.tempSelectionsRef,
    runSet.groupSelectionsRef
  );

  const tempSelectNone = ViewHooks.useViewActionBindAll(
    TempSelectionsActions.selectNone,
    props.tempSelectionsRef,
    runSet.groupSelectionsRef
  );

  const entityName = runSet.project
    ? runSet.project.entityName
    : props.pageEntityName;

  const projectName = runSet.project
    ? runSet.project.name
    : props.pageProjectName;

  const groupTempSelection = useMemo(
    () => ({
      grouping: groupSelections.grouping,
      selections: tempSelections,
      expandedRowAddresses: groupSelections.expandedRowAddresses,
    }),
    [
      groupSelections.grouping,
      groupSelections.expandedRowAddresses,
      tempSelections,
    ]
  );

  const {mergeFilters, sweep, report} = useRunsQueryContext();

  const runSelectorQuery = useRunSelectorQuery({
    entityName,
    projectName,
    filters: runSetFilters,
    mergeFilters,
    groupTempSelection,
    ...props,
  });

  const {tempSelectedCount, tempOutputArtifactsCount, tempSelectedActiveCount} =
    useSelectedCounts(props.runSetRef, props.tempSelectionsRef);

  const [deleteRun] = Generated.useDeleteRunMutation();
  const [stopRun] = Generated.useStopRunMutation();
  const updateRun = RunHooks.useUpdateRun();
  const updateRunTags = RunHooks.useUpdateRunTags();
  const bulkUpdateRunTags = RunHooks.useBulkUpdateRunTags();
  const [modifyRuns] = Generated.useModifyRunsMutation();
  const [moveRuns] = Generated.useMoveRunsMutation();
  const [deleteRuns] = Generated.useDeleteRunsMutation();
  const [updateGroupRun] = Generated.useUpsertRunGroupMutation();
  const updateGroupRunTags = RunHooks.useUpdateGroupRunTags(
    projectName,
    entityName
  );

  const runSetUpdate = ViewHooks.useViewAction(
    props.runSetRef,
    RunSetActions.update
  );

  const setGrouping = ViewHooks.useViewAction(
    runSet.groupSelectionsRef,
    GroupSelectionsActions.setGrouping
  );

  const setSort = ViewHooks.useViewAction(runSet.sortRef, SortActions.set);

  const selectAll = ViewHooks.useViewAction(
    runSet.groupSelectionsRef,
    GroupSelectionsActions.selectAll
  );

  const selectNone = ViewHooks.useViewAction(
    runSet.groupSelectionsRef,
    GroupSelectionsActions.selectNone
  );

  const setProject = useCallback(
    (newEntityName: string, newProjectName: string) =>
      runSetUpdate({
        project: {entityName: newEntityName, name: newProjectName},
      }),
    [runSetUpdate]
  );

  const setFilters = ViewHooks.useViewAction(
    runSet.filtersRef,
    FilterActions.set
  );

  const setSearch = useCallback(
    (query: string) => runSetUpdate({search: {query}}),
    [runSetUpdate]
  );

  const setTableSettings = useCallback(
    (runFeed: TableSettings) => runSetUpdate({runFeed}),
    [runSetUpdate]
  );

  const dispatch = useDispatch();

  const queryVariables = useMemo(() => {
    return {
      id: runSet.id,
      filters:
        mergeFilters != null
          ? Filter.mergedFilters(runSetFilters, mergeFilters)
          : Filter.mergedFilters(runSetFilters),
      selections: SelectionManager.toRunFilter(groupSelections),
      sort,
      grouping: groupSelections.grouping,
    } as Query.Query;
  }, [runSet.id, mergeFilters, runSetFilters, groupSelections, sort]);

  return {
    bulkUpdateRunTags,
    deleteRun,
    deleteRuns,
    dispatch,
    entityName,
    filters: runSetFilters,
    groupSelections,
    groupTempSelection,
    mergeFilters,
    modifyRuns,
    moveRuns,
    projectName,
    queryVariables,
    report,
    runSelectorQuery,
    runSet,
    runSetFull,
    selectAll,
    selectNone,
    setFilters,
    setGrouping,
    setProject,
    setSearch,
    setSort,
    setTableSettings,
    sort,
    stopRun,
    sweep,
    tableSettings: runSet.runFeed,
    tempOutputArtifactsCount,
    tempSelectAll,
    tempSelectedActiveCount,
    tempSelectedCount,
    tempSelections,
    tempSelectNone,
    toggleExpandedRow,
    updateGroupRun,
    updateGroupRunTags,
    updateRun,
    updateRunTags,
    viewer,
  };
}

export const RunSelectorInner = (props: RunSelectorInnerProps) => {
  const selectedProps = useRunSelectorProps(props);
  return <RunSelectorInnerComp {...props} {...selectedProps} />;
};
